import React, { useState, useEffect } from 'react';
import { Auth, API, Storage } from 'aws-amplify';
import awsconfig from './aws-exports';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import './ConfigForm.css';
import DOMPurify from 'dompurify';

API.configure(awsconfig);

const ConfigForm = () => {
  const [form, setForm] = useState({
    title: '',
    displayTitle: '',
    type: '',
    filename1: '',
    filename2: '',
    columns: [{ standardName: '', nameInFile1: '', nameInFile2: '', dataType: '' }],
    sideAName: '',
    isSideANameCustom: false,
    sideBName: '',
    isSideBNameCustom: false,
  });
  


  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [existingTitles, setExistingTitles] = useState([]);
  const [existingFolders, setExistingFolders] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTitleDuplicate, setIsTitleDuplicate] = useState(false);
  const [file1Headers, setFile1Headers] = useState([]);
  const [file2Headers, setFile2Headers] = useState([]);
  const [file1DataTypes, setFile1DataTypes] = useState({});
  const [file2DataTypes, setFile2DataTypes] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false); 
  
  const navigate = useNavigate();


  const fetchTitles = async () => {
    const apiName = 'ConfigTitles';
    const path = '/configtitles';
    try {
      const user = await Auth.currentAuthenticatedUser();
      const organization = user.attributes['email'].split('@')[1].split('.')[0];
  
      const requestBody = {
        organization: organization,
      };
  
      const response = await API.post(apiName, path, { body: requestBody });
  
      if (response) {
        // Set the titles and folders from the response
        setExistingTitles(response.titles || []); // Set to an empty array if undefined
        setExistingFolders(response.folders || []); // Set to an empty array if undefined
        console.log("folders", existingFolders)
        console.log("titles", existingTitles)
      }
    } catch (err) {
      console.error('Error fetching titles and folders:', err);
    }
  };
  

  const addColumn = () => {
    setForm(prevState => ({
      ...prevState,
      columns: [...prevState.columns, { standardName: '', nameInFile1: '', nameInFile2: '', dataType: '' }],
    }));
  };
  

  const isHeaderSelected = (header, columnName) => {
    return form.columns.some(column => column[columnName] === header);
  };
  
  const headerSelectionCount = (header, columnName) => {
    return form.columns.reduce((count, column) => count + (column[columnName] === header ? 1 : 0), 0);
  };
  


  const handleRemoveClick = (index, event) => {
    event.preventDefault();
    const list = [...form.columns];
    list.splice(index, 1);
    setForm(prevState => ({ ...prevState, columns: list }));
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    setForm(prevState => ({
      ...prevState,
      type: type,
      
    }));
  };





  const sanitizeInput = (value) => {
    // Use DOMPurify for sanitization
    return DOMPurify.sanitize(value);
  };
  
  const validateLength = (value, maxLength = 50) => {
    return value.length <= maxLength;
  };
  
  const handleTitleChange = (e) => {
    const value = sanitizeInput(e.target.value); // Keep the user's input as is for displayTitle
    const sanitizedValue = value.replace(/\s/g, ""); // Remove spaces for the internal title
    
    if (validateLength(value)) { // Validate based on the length of the user's input
      setIsTitleDuplicate(existingTitles.includes(sanitizedValue)); // Check duplicates based on sanitized title
      setForm((prevState) => ({
        ...prevState,
        displayTitle: value, // Save the display title with spaces
        title: sanitizedValue, // Save the title without spaces
      }));
    }
  };
  
  
  
  
  const hasDuplicateStandardNames = () => {
    const standardNames = form.columns.map(column => column.standardName);
    const uniqueStandardNames = new Set(standardNames);
    return uniqueStandardNames.size !== standardNames.length;
  };
  

  const handleInputChange = (e, index) => {
    const { name, value, type, checked } = e.target;
    const sanitizedValue = DOMPurify.sanitize(value);
    const list = [...form.columns];
  
    // Handle "multi" data type (store as a comma-separated string)
    if (list[index].dataType === 'multi' && (name === 'nameInFile1' || name === 'nameInFile2')) {
      const currentValues = list[index][name] ? list[index][name].split(',') : [];
      if (checked) {
        // Add the selected value if checked
        list[index][name] = [...currentValues, sanitizedValue].join(',');
      } else {
        // Remove the selected value if unchecked
        list[index][name] = currentValues.filter(item => item !== sanitizedValue).join(',');
      }
    } else {
      // Handle other data types
      list[index][name] = sanitizedValue;
    }
  
    // Update the form state
    setForm(prevState => ({ ...prevState, columns: list }));
  };
  
  



  
  const validFileTypes = [
    'text/csv',
    'application/json', // JSON files
    'text/plain', // TXT files
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/octet-stream'
  ];
  
  const maxFileSize = 500000 * 1024 * 1024; // 5MB
  
  const isValidFileType = (file) => validFileTypes.includes(file.type);
  const isValidFileSize = (file) => file.size <= maxFileSize;
  
  const handleFileChange1 = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    if (!isValidFileType(file)) {
      alert('Invalid file type. Please upload a CSV, JSON, or TXT file.');
      return;
    }
  
    if (!isValidFileSize(file)) {
      alert('File size exceeds the maximum limit.');
      return;
    }
  
    const reader = new FileReader();
    reader.onload = () => {
      if (file.type === 'application/json') {
        try {
          const jsonContent = JSON.parse(reader.result);
          console.log('Parsed JSON:', jsonContent);
        } catch (err) {
          alert('Error parsing JSON file.');
        }
      } else if (file.type === 'text/plain') {
        console.log('Text file content:', reader.result);
      }
    };
  
    reader.readAsText(file); // Read file content
    setFile1(file);
    setForm(prevState => ({ ...prevState, filename1: file.name }));
  };
  
  const handleFileChange2 = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    if (!isValidFileType(file)) {
      alert('Invalid file type. Please upload a CSV, JSON, or TXT file.');
      return;
    }
  
    if (!isValidFileSize(file)) {
      alert('File size exceeds the maximum limit.');
      return;
    }
  
    const reader = new FileReader();
    reader.onload = () => {
      if (file.type === 'application/json') {
        try {
          const jsonContent = JSON.parse(reader.result);
          console.log('Parsed JSON:', jsonContent);
        } catch (err) {
          alert('Error parsing JSON file.');
        }
      } else if (file.type === 'text/plain') {
        console.log('Text file content:', reader.result);
      }
    };
  
    reader.readAsText(file); // Read file content
    setFile2(file);
    setForm(prevState => ({ ...prevState, filename2: file.name }));
  };

  const sendFiles = async () => {
    console.log("file1", file1)

    console.log("file2",file2)
    try {
      if (file1 && file2) {
        setIsProcessing(true); // Start processing
        console.log(file1)
  
        await Storage.put(file1.name, file1);
        await Storage.put(file2.name, file2);
  
        // Trigger your lambda function here
        const apiName = 'fileuploadapi';
        const path = '/filematch';
        const init = {
          body: {
            file1Name: file1.name,
            file2Name: file2.name
          },
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        };
  
        const response = await API.post(apiName, path, init);
        
  
        // Update form state based on the response
        const newColumns = response.matchingColumns.map(column => ({
          standardName: column.standardName,
          nameInFile1: column.nameInFile1,
          nameInFile2: column.nameInFile2,
          dataType: response.file1DataTypes[column.nameInFile1] || response.file2DataTypes[column.nameInFile2] || ''
        }));

        console.log(newColumns)
  
        // Update form state with the new columns and data types
        setForm(prevState => ({
          ...prevState,
          columns: newColumns
        }));
  
        setFile1Headers(response.file1Headers);
        setFile2Headers(response.file2Headers);
        setFile1DataTypes(response.file1DataTypes);
        setFile2DataTypes(response.file2DataTypes);
      } else {
        
      }
    } catch (err) {
      
    } finally {
      setIsProcessing(false); // End processing
    }
  };
  

  const hasDuplicateSelections = () => {
    const file1Selections = form.columns.map(column => column.nameInFile1);
    const file2Selections = form.columns.map(column => column.nameInFile2);
  
    const hasDuplicates = (arr) => new Set(arr).size !== arr.length;
  
    return hasDuplicates(file1Selections) || hasDuplicates(file2Selections);
  };
  const areAllDataTypesPopulated = () => {
    return form.columns.every(column => column.dataType !== '');
  };
  
  



  useEffect(() => {
    fetchTitles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!form.title || !form.displayTitle || !form.type || !form.filename1 || !form.filename2 || form.columns.length === 0) {
      alert('Please fill all the fields');
      return;
    }
  
    if (!form.sideAName || !form.sideBName) {
      alert('Please set names for both Side A and Side B.');
      return;
    }
  
    if (hasDuplicateSelections()) {
      alert('Each header can only match to one header on the other side. Please resolve duplicate selections.');
      return;
    }
  
    if (hasDuplicateStandardNames()) {
      alert('Standard names must be unique. Please resolve duplicates.');
      return;
    }
  
    if (!areAllDataTypesPopulated()) {
      alert('Please ensure all Data Types are populated.');
      return;
    }
  
    setLoading(true);
  
    try {
      const user = await Auth.currentAuthenticatedUser();
      const organization = user.attributes['email'].split('@')[1].split('.')[0];
      const email = user.attributes['email'];
      const userPoolId = (await Auth.currentUserPoolUser()).pool.userPoolId; // Retrieve User Pool ID
  
      const apiName = 'configapi';
      const path = '/config';
      const init = {
        body: {
          ...form,
          columns: form.columns.map(column => ({
            ...column,
            nameInFile1: column.dataType === 'multi' ? column.nameInFile1.split(',') : column.nameInFile1,
            nameInFile2: column.dataType === 'multi' ? column.nameInFile2.split(',') : column.nameInFile2,
          })),
          organization,
          email,
          title: form.title,
          displayTitle: form.displayTitle,
          userPoolId,
        },
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };
      console.log("User Pool ID:", userPoolId);
      console.log(email)
      const response = await API.post(apiName, path, init);
      setLoading(false);
      setFormSubmitted(true);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };
  
  
  
  
  

  const navigateToReconciliationPage = () => {
    navigate(`/reconciliation/${form.title}`);
  };
  
  

  return (
    <div className="config-form-container">
      <Link to="/" className="back-to-home-btn">Back to Home</Link>
      <h2>Create Configuration</h2>
      <hr className="divider-line" /> 
  
      <form className="config-form">
  <div className="full-width-group">
    <label>Title</label>
    <input
      type="text"
      value={form.displayTitle}
      onChange={handleTitleChange}
      className="full-width"
    />
    {isTitleDuplicate && (
      <span className="error">This title already exists. Please choose a different one.</span>
    )}
  </div>

  <div className="full-width-group">
    <label>Reconciliation Type</label>
    <select
      value={form.type}
      onChange={handleTypeChange}
      className="full-width"
    >
      <option value="">Select Type</option>
      <option value="trade">Trade</option>
      <option value="position">Position</option>
      <option value="cash">Cash</option>
      <option value="bank">Bank</option>
      <option value="fee">Fee</option>
      <option value="other">Other</option>
      <option value="trade surveillance">Trade Surveillance</option>
    </select>
  </div>



        <hr className="divider-line" /> 
        
      
        <div className="file-info-section">
  {/* Side A Name Dropdown */}
  <div className="input-group">
    <label>Side A Name</label>
    {form.isSideANameCustom ? (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Enter a new name"
          value={form.sideAName || ''}
          onChange={(e) =>
            setForm((prevState) => ({ ...prevState, sideAName: e.target.value }))
          }
        />
        <button
          type="button"
          onClick={() =>
            setForm((prevState) => ({
              ...prevState,
              isSideANameCustom: false,
            }))
          }
          style={{ marginLeft: '10px' }}
        >
          ▼
        </button>
      </div>
    ) : (
      <select
        value={form.sideAName || ''}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'newName') {
            setForm((prevState) => ({
              ...prevState,
              isSideANameCustom: true,
              sideAName: '',
            }));
          } else {
            setForm((prevState) => ({ ...prevState, sideAName: value }));
          }
        }}
      >
        <option value="">Select a Name</option>
        {existingFolders.map((folder) => (
          <option key={folder} value={folder.replace(/\/$/, '')}>
            {folder.replace(/\/$/, '')}
          </option>
        ))}
        <option value="newName">Enter New Name</option>
      </select>
    )}
  </div>

  {/* Side B Name Dropdown */}
  <div className="input-group">
    <label>Side B Name</label>
    {form.isSideBNameCustom ? (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Enter a new name"
          value={form.sideBName || ''}
          onChange={(e) =>
            setForm((prevState) => ({ ...prevState, sideBName: e.target.value }))
          }
        />
        <button
          type="button"
          onClick={() =>
            setForm((prevState) => ({
              ...prevState,
              isSideBNameCustom: false,
            }))
          }
          style={{ marginLeft: '10px' }}
        >
          ▼
        </button>
      </div>
    ) : (
      <select
        value={form.sideBName || ''}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'newName') {
            setForm((prevState) => ({
              ...prevState,
              isSideBNameCustom: true,
              sideBName: '',
            }));
          } else {
            setForm((prevState) => ({ ...prevState, sideBName: value }));
          }
        }}
      >
        <option value="">Select a Name</option>
        {existingFolders.map((folder) => (
          <option key={folder} value={folder.replace(/\/$/, '')}>
            {folder.replace(/\/$/, '')}
          </option>
        ))}
        <option value="newName">Enter New Name</option>
      </select>
    )}
  </div>
</div>



        
  
<div className="file-processing-section">
  {/* Side A File Selection */}
  <div className="input-group">
    <label>
      {form.sideAName
        ? `${form.sideAName} Sample File`
        : "Side A Sample File"}
      : {form.filename1 ? form.filename1 : "No file selected"}
    </label>
    <input type="file" onChange={handleFileChange1} />
  </div>

  {/* Side B File Selection */}
  <div className="input-group">
    <label>
      {form.sideBName
        ? `${form.sideBName} Sample File`
        : "Side B Sample File"}
      : {form.filename2 ? form.filename2 : "No file selected"}
    </label>
    <input type="file" onChange={handleFileChange2} />
  </div>

  <button type="button" onClick={sendFiles} disabled={isProcessing}>
    {isProcessing ? "Mapping..." : "Map File Headers"}
  </button>
</div>


<hr className="divider-line" /> 
 
  
{form.columns.map((x, i) => {
  return (
    <div className="box" key={i}>
      <div className="input-row">
        {/* Standard Name Selection */}
        <div className="input-group">
          <label>Standard Name</label>
          <select
            className="input-field"
            name="standardName"
            value={x.standardName}
            onChange={e => handleInputChange(e, i)}
          >
            <option value="">Select Standard Name</option>
            {x.dataType === 'multi' ? (
              <>
                {(x.nameInFile1 || '').split(',').map(header => (
                  <option key={header} value={header}>
                    {header}
                  </option>
                ))}
                {(x.nameInFile2 || '').split(',').map(header => (
                  <option key={header} value={header}>
                    {header}
                  </option>
                ))}
              </>
            ) : (
              <>
                {x.nameInFile1 && <option value={x.nameInFile1}>{x.nameInFile1}</option>}
                {x.nameInFile2 && <option value={x.nameInFile2}>{x.nameInFile2}</option>}
              </>
            )}
          </select>
        </div>



        <div className="input-group">
  <label>Name in File 1</label>
  {x.dataType === 'multi' ? (
    <div className="multi-dropdown">
      <button
        type="button"
        className="multi-dropdown-btn"
        onClick={() => {
          const dropdown = document.getElementById(`multi-dropdown-file1-${i}`);
          dropdown.style.display =
            dropdown.style.display === "block" ? "none" : "block";
        }}
      >
        {x.nameInFile1 ? x.nameInFile1.split(",").join(", ") : "Select Columns"}
      </button>
      <div id={`multi-dropdown-file1-${i}`} className="multi-dropdown-menu">
        {file1Headers.map((header) => (
          <label key={header} className="multi-dropdown-option">
            <input
              type="checkbox"
              name="nameInFile1"
              value={header}
              checked={(x.nameInFile1 || "").split(",").includes(header)}
              onChange={(e) => handleInputChange(e, i)}
            />
            {header}
          </label>
        ))}
      </div>
    </div>
  ) : (
    <select
      name="nameInFile1"
      value={x.nameInFile1}
      onChange={(e) => handleInputChange(e, i)}
    >
      <option value="">Select a column</option>
      {file1Headers.map((header) => {
        const selectionCount = headerSelectionCount(header, "nameInFile1");
        return (
          <option key={header} value={header}>
            {header} {"✓".repeat(selectionCount)}
          </option>
        );
      })}
    </select>
  )}
</div>

<div className="input-group">
  <label>Name in File 2</label>
  {x.dataType === "multi" ? (
    <div className="multi-dropdown">
      <button
        type="button"
        className="multi-dropdown-btn"
        onClick={() => {
          const dropdown = document.getElementById(`multi-dropdown-file2-${i}`);
          dropdown.style.display =
            dropdown.style.display === "block" ? "none" : "block";
        }}
      >
        {x.nameInFile2 ? x.nameInFile2.split(",").join(", ") : "Select Columns"}
      </button>
      <div id={`multi-dropdown-file2-${i}`} className="multi-dropdown-menu">
        {file2Headers.map((header) => (
          <label key={header} className="multi-dropdown-option">
            <input
              type="checkbox"
              name="nameInFile2"
              value={header}
              checked={(x.nameInFile2 || "").split(",").includes(header)}
              onChange={(e) => handleInputChange(e, i)}
            />
            {header}
          </label>
        ))}
      </div>
    </div>
  ) : (
    <select
      name="nameInFile2"
      value={x.nameInFile2}
      onChange={(e) => handleInputChange(e, i)}
    >
      <option value="">Select a column</option>
      {file2Headers.map((header) => {
        const selectionCount = headerSelectionCount(header, "nameInFile2");
        return (
          <option key={header} value={header}>
            {header} {"✓".repeat(selectionCount)}
          </option>
        );
      })}
    </select>
  )}
</div>








        {/* Data Type Selection */}
        <div className="input-group">
          <label>Data Type</label>
          <select name="dataType" value={x.dataType} onChange={e => handleInputChange(e, i)}>
            <option value="">Select Data Type</option>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="multi">Multi</option>
          </select>
        </div>
      </div>

      {/* Add/Remove Buttons */}
      <div className="btn-box">
        {form.columns.length !== 1 && (
          <button type="button" className="mr10" onClick={event => handleRemoveClick(i, event)}>
            Remove
          </button>
        )}
        {form.columns.length - 1 === i && (
          <button type="button" onClick={addColumn} className="add-btn">
            Add
          </button>
        )}
      </div>
    </div>
  );
})}

{/* Form Buttons */}
<div className="config-form-button-group">
  <button className="submit-btn" type="button" onClick={handleSubmit} disabled={loading || isTitleDuplicate}>
    {loading ? 'Loading...' : 'Submit'}
  </button>

  {formSubmitted && (
    <button className="submit-btn" onClick={navigateToReconciliationPage}>
      Open Recon
    </button>
  )}
</div>



      </form>
    </div>
  );
  


};



export default ConfigForm;

